import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Box,
  Switch,
  Avatar,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { LoadingButton } from "@mui/lab";
import { useThumbzApi } from "src/services/thumbz-api";
import { useAppDispatch, useAppSelector } from "src/app/hooks";
import {
  approvalEditSlice,
  approvalEditThunks,
} from "src/features/approval-edit/approvalEditSlice";
import {
  IApprovalStageCustomer,
  IApprovalItemRevisionAirStatusEnum,
  IApprovalItemRevision,
} from "src/services/thumbz-base-api";
import { removeHTML } from "src/utils/remove-html";
import toast from "react-hot-toast";
import { getServerErrorArrayMessage } from "src/utils/get-server-error-message";

interface ForceApproveCustomerRequest {
  asc_id: string;
  reason: string;
  revision_ids?: string[];
}

interface ForceApprovalModalProps {
  open: boolean;
  onClose: () => void;
  approvalStageCustomer: IApprovalStageCustomer;
}

export const ForceApprovalModal: React.FC<ForceApprovalModalProps> = ({
  open,
  onClose,
  approvalStageCustomer,
}) => {
  const { api } = useThumbzApi();
  const dispatch = useAppDispatch();
  const approval = useAppSelector(approvalEditSlice.selectors.approval);

  const pendingRevisions: IApprovalItemRevision[] = React.useMemo(() => {
    return approvalStageCustomer.revisions.filter(
      (r) => r.air_status === IApprovalItemRevisionAirStatusEnum.Pending,
    );
  }, [approvalStageCustomer.revisions]);

  const [reason, setReason] = React.useState<string>("");
  const [advanced, setAdvanced] = React.useState<boolean>(false);
  const [selectedIds, setSelectedIds] = React.useState<string[]>(
    pendingRevisions.map((r) => r.air_id),
  );
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  React.useEffect(() => {
    setSelectedIds(pendingRevisions.map((r) => r.air_id));
  }, [pendingRevisions, open]);

  const minReasonOk = reason.trim().length >= 10;

  function toggleSelected(id: string) {
    const exists = selectedIds.includes(id);
    const next = exists ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
    setSelectedIds(next);
  }

  async function handleSubmit() {
    if (!approval?.apv_id) return;
    setSubmitting(true);
    const body: ForceApproveCustomerRequest = {
      asc_id: approvalStageCustomer.asc_id,
      reason: reason.trim(),
      revision_ids: advanced ? selectedIds : undefined,
    };

    try {
      await api.approvalStage.approvalStageControllerForceApproveCustomer(
        { apvId: approval.apv_id },
        body,
      );
      toast.success("Aprovação forçada com sucesso");
      await dispatch(approvalEditThunks.fetchApproval({ apv_id: approval.apv_id }));
      onClose();
    } catch (error) {
      getServerErrorArrayMessage(error).forEach((m) => toast.error(m));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} aria-describedby="force-approve-helper">
      <DialogTitle>Forçar Aprovação do Aprovador</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack spacing={0.5} sx={{ p: 2, borderRadius: 1, bgcolor: "warning.light" }}>
            <Typography variant="subtitle2">Atenção: Aprovação Administrativa</Typography>
            <Typography variant="body2" id="force-approve-helper">
              Esta ação aprovará materiais em nome do aprovador selecionado.
            </Typography>
          </Stack>

          <Typography variant="subtitle2">Motivo da aprovação forçada *</Typography>
          <TextField
            multiline
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText={
              !minReasonOk && reason.trim().length > 0
                ? `Informe ao menos 10 caracteres — ${reason.trim().length}/500`
                : `${reason.trim().length}/500`
            }
            error={!minReasonOk && reason.trim().length > 0}
            inputProps={{ maxLength: 500 }}
            sx={{
              "& .MuiFilledInput-root": {
                paddingTop: 1,
                paddingBottom: 1,
              },
            }}
          />

          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="subtitle2">Revisões para aprovar</Typography>
              <Tooltip
                title={
                  "Escolha manualmente quais revisões serão aprovadas. Se desmarcado, todas as pendentes serão aprovadas."
                }
              >
                <InfoOutlined fontSize="small" />
              </Tooltip>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} />
              <Typography variant="body2">Selecionar revisões manualmente</Typography>
            </Stack>
          </Stack>

          {advanced && (
            <FormGroup>
              {pendingRevisions.map((rev) => {
                const firstAsset = rev.approvalitem?.approvalItemAsset?.[0];
                const rawDescription = rev.approvalitem?.item_description || "";
                const sanitizedDescription = removeHTML(rawDescription).trim();
                const titleText =
                  sanitizedDescription.length > 0
                    ? sanitizedDescription
                    : firstAsset?.asset?.ast_original_filename || "Sem título";

                return (
                  <FormControlLabel
                    key={rev.air_id}
                    control={
                      <Checkbox
                        checked={selectedIds.includes(rev.air_id)}
                        onChange={() => toggleSelected(rev.air_id)}
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">{titleText}</Typography>
                      </Stack>
                    }
                  />
                );
              })}
            </FormGroup>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton color="inherit" onClick={onClose}>
          Cancelar
        </LoadingButton>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          disabled={!minReasonOk || (advanced && selectedIds.length === 0) || submitting}
          loading={submitting}
        >
          Confirmar Aprovação
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ForceApprovalModal;
